import { gsap } from '../gsap-config.js';
import { ARTICLE_RAIL_LAYOUT, prefersReducedMotion } from '../utils/media.js';

const SHEET_DURATION = 0.4;

class ArticleNavDrawer extends HTMLElement {
	#abort = null;
	#dialog = null;
	#panel = null;
	#sheet = null;
	#slot = null;
	#chip = null;
	#mql = null;
	#closing = false;

	connectedCallback() {
		this.#panel = this.querySelector('[data-article-nav-panel]');
		this.#dialog = this.querySelector('[data-article-nav-dialog]');
		this.#sheet = this.#dialog?.querySelector('.article-nav-dialog__panel');
		this.#slot = this.querySelector('[data-article-nav-slot]');
		this.#chip = this.querySelector('[data-article-nav-open]');
		if (!this.#panel || !this.#dialog || !this.#sheet || !this.#slot || !this.#chip) return;

		this.#abort = new AbortController();
		const { signal } = this.#abort;

		this.#chip.addEventListener('click', () => this.#open(), { signal });
		this.querySelector('[data-article-nav-close]')?.addEventListener('click', () => this.#close(), { signal });
		this.#dialog.addEventListener('click', (event) => {
			if (event.target === this.#dialog) this.#close();
		}, { signal });
		this.#dialog.addEventListener('cancel', (event) => {
			event.preventDefault();
			this.#close();
		}, { signal });
		this.#dialog.addEventListener('close', () => this.#onDialogClose(), { signal });
		this.#slot.addEventListener('click', (event) => {
			if (event.target.closest('a[href^="#"]')) this.#close();
		}, { signal });

		this.#mql = window.matchMedia(ARTICLE_RAIL_LAYOUT);
		this.#mql.addEventListener('change', () => this.#syncMode(), { signal });
		this.#syncMode();
	}

	disconnectedCallback() {
		this.#close({ immediate: true });
		this.#restorePanel();
		this.#restoreChip();
		this.#abort?.abort();
	}

	#syncMode() {
		if (this.#mql.matches) {
			this.#close({ immediate: true });
			this.#restorePanel();
			this.#restoreChip();
			this.classList.remove('is-drawer');
			this.#chip.hidden = true;
			this.#chip.setAttribute('aria-expanded', 'false');
			return;
		}

		this.#movePanelToDialog();
		this.#moveChipToBody();
		this.classList.add('is-drawer');
		this.#chip.hidden = false;
		this.#syncChipLift();
	}

	#movePanelToDialog() {
		if (this.#panel.parentElement !== this.#slot) {
			this.#slot.append(this.#panel);
		}
	}

	#restorePanel() {
		if (this.#panel && this.#panel.parentElement !== this) {
			this.append(this.#panel);
		}
	}

	#moveChipToBody() {
		if (this.#chip.parentElement !== document.body) {
			document.body.append(this.#chip);
		}
	}

	#restoreChip() {
		if (!this.#chip || this.#chip.parentElement === this) return;
		this.#chip.style.removeProperty('transform');
		if (this.#dialog) {
			this.#dialog.before(this.#chip);
			return;
		}
		this.prepend(this.#chip);
	}

	#syncChipLift() {
		const height = Number.parseFloat(
			getComputedStyle(document.documentElement).getPropertyValue('--article-cta-offset')
		) || 0;
		this.#chip.style.transform = height
			? `translate3d(0, ${-height}px, 0)`
			: 'translate3d(0, 0, 0)';
	}

	#open() {
		if (this.#dialog.open || this.#closing) return;

		this.#dialog.showModal();
		document.documentElement.classList.add('tt-no-scroll');
		this.#chip.setAttribute('aria-expanded', 'true');
		gsap.killTweensOf(this.#sheet);

		if (prefersReducedMotion()) {
			gsap.set(this.#sheet, { yPercent: 0 });
			this.#dialog.classList.add('is-open');
			return;
		}

		void this.#sheet.offsetHeight;
		gsap.set(this.#sheet, { yPercent: 100 });
		gsap.to(this.#sheet, {
			yPercent: 0,
			duration: SHEET_DURATION,
			ease: 'power2.out',
		});
		requestAnimationFrame(() => {
			this.#dialog.classList.add('is-open');
		});
	}

	#close({ immediate = false } = {}) {
		if (!this.#dialog?.open || this.#closing) return;

		const finish = () => {
			if (this.#dialog.open) this.#dialog.close();
		};

		gsap.killTweensOf(this.#sheet);

		if (immediate || prefersReducedMotion()) {
			this.#dialog.classList.remove('is-open');
			gsap.set(this.#sheet, { yPercent: 100 });
			finish();
			return;
		}

		this.#closing = true;
		this.#dialog.classList.remove('is-open');
		gsap.to(this.#sheet, {
			yPercent: 100,
			duration: SHEET_DURATION,
			ease: 'power2.in',
			onComplete: () => {
				this.#closing = false;
				finish();
			},
		});
	}

	#onDialogClose() {
		this.#closing = false;
		this.#dialog?.classList.remove('is-open');
		gsap.killTweensOf(this.#sheet);
		gsap.set(this.#sheet, { yPercent: 100 });
		document.documentElement.classList.remove('tt-no-scroll');
		this.#chip?.setAttribute('aria-expanded', 'false');
	}
}

customElements.define('article-nav-drawer', ArticleNavDrawer);
